import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

import { di } from 'react-magnetic-di';

import { createBuiltInColorMap, readableTextColor } from '@atlassian/color-picker';
import { generateThemeBranding } from '@atlassian/csm-colour-theme-generator/generate-theme-branding';

import {
	HUB_PRIMARY_COLOR_CSS_VAR,
	HUB_PRIMARY_LINK_COLOR_CSS_VAR,
	HUB_PRIMARY_TEXT_COLOR_CSS_VAR,
} from '../../constants';
import { useHubContext } from '../../utils';
import { HUB_COLOR_CSS_VARS, useHubBrandPalette } from '../../utils/hub-brand-palette';
import type { PaletteRef } from '../../utils/hub-brand-palette';
import { HUB_CORNER_RADIUS_CSS_VAR, resolveHubCornerRadius } from '../../utils/hub-corner-radius';
import type { SpaceDensity } from '../../utils/hub-space-density/types';
import type { HubTypography } from '../../utils/hub-typography';
import { getHubDefaultParameters } from '../get-hub-default-parameters';
import { useIsAdvancedThemingEnabled } from '../use-is-advanced-theming-enabled';

import type { HubThemingContextValue } from './types';

const DEFAULT_PRIMARY = '#003172';

// Fixed hex values for the 4 neutral border swatches surfaced alongside
// the brand palette in the border picker. Dark mode is out of scope for
// company-hub today, so the picker stores raw hex (no token indirection,
// no getComputedStyle reads) — keeps storage simple and SSR-deterministic.
export const HUB_BORDER_NEUTRAL_HEX = '#DCDFE4'; // matches `color.border`
export const HUB_BORDER_NEUTRAL_BOLD_HEX = '#758195'; // matches `color.border.bold`
// Neutral light gray. Used as the day-0 accent when neither the BE has
// a stored `secondaryColor` nor the admin has picked one in the panel.
// Matches `color.border` so accent-derived shades stay visually muted
// until the admin opts in to a colorful accent.
const DEFAULT_ACCENT = '#DDDEE1';

const HubThemingContext: React.Context<HubThemingContextValue | null> =
	createContext<HubThemingContextValue | null>(null);

const readableHubTextColor = (color: string): string =>
	readableTextColor(color, '@atlassian/company-hub-common');

export const HubThemingProvider = ({ children }: { children: ReactNode }): JSX.Element => {
	di(useHubContext, useIsAdvancedThemingEnabled);
	const { branding } = useHubContext();
	const isEnabled = useIsAdvancedThemingEnabled();
	const primaryColor = branding?.colors?.primary ?? DEFAULT_PRIMARY;

	// Accent comes from the BE (`HelpCenterBrandingColors.secondaryColor`,
	// mapped to `branding.colors.accent` at the bootstrap boundary). If the
	// BE returns nothing — pre-v2 hubs, non-help-center hosts, fresh tenants
	// — we fall through to `DEFAULT_ACCENT`. The panel persists changes via
	// the same Relay mutation path that primary uses, so the picker's
	// `value` prop and the provider's `accentColor` here stay in lockstep
	// via the next bootstrap re-render. No FE-side state needed.
	const accentColor = branding?.colors?.accent ?? DEFAULT_ACCENT;

	const palette = useHubBrandPalette({ primary: primaryColor, accent: accentColor });

	const primaryTextColor = useMemo(() => readableHubTextColor(primaryColor), [primaryColor]);
	const primaryLinkColor = useMemo(
		() => generateThemeBranding(primaryColor).color900,
		[primaryColor],
	);

	// Typography source of truth is the BE-persisted value, surfaced on
	// `branding.typography` at the bootstrap boundary. This is what makes the
	// help-seeker VIEW pages (homepage, profile, login, request list/details)
	// render the admin's chosen font — they read the same branding the editor
	// does. `setHubTypography` layers an in-session override on top for instant
	// edit-mode preview; the override is persisted on Publish via the branding
	// mutation (see action-bar), after which a bootstrap re-read makes the base
	// value match. View mode never calls the setter, so it always shows the
	// persisted value. Undefined (no BE value, no override) → HubTypographyScope
	// no-ops → Atlassian Sans default.
	const brandingTypography = branding?.typography;
	const [typographyOverride, setTypographyOverride] = useState<HubTypography | undefined>(
		undefined,
	);
	const typography = typographyOverride ?? brandingTypography;
	const setHubTypography = useCallback((next: HubTypography) => {
		setTypographyOverride(next);
	}, []);

	const brandingSpaceDensity = branding?.spaceDensity;
	const [spaceDensityOverride, setSpaceDensityOverride] = useState<SpaceDensity | undefined>(
		undefined,
	);
	const spaceDensity = spaceDensityOverride ?? brandingSpaceDensity;
	const setSpaceDensity = useCallback((next: SpaceDensity) => {
		setSpaceDensityOverride(next);
	}, []);

	// Corner radius source of truth is the BE-persisted value, surfaced on
	// `branding.cornerRadius` at the bootstrap boundary. `setHubCornerRadius`
	// layers an in-session override on top for instant edit-mode preview; the
	// override is persisted on Publish via the branding mutation. View mode
	// never calls the setter. Undefined / invalid → resolves to 8px default.
	const brandingCornerRadius = branding?.cornerRadius;
	const [cornerRadiusOverride, setCornerRadiusOverride] = useState<number | undefined>(undefined);
	const cornerRadius = resolveHubCornerRadius(cornerRadiusOverride ?? brandingCornerRadius);
	const setHubCornerRadius = useCallback((next: number) => {
		setCornerRadiusOverride(next);
	}, []);

	// `defaultParameters` and `brandBackgroundColorMap` deliberately split on
	// `isEnabled` so consumers (CommonHubExtensionsDependenciesProvider, the
	// extension config panels) can keep reading from `useHubTheming()` without
	// branching themselves. When the gate is OFF we restore the pre-v2 shape
	// exactly: legacy CSS-var refs in default params (consumed by
	// setHubBrandingStyle's writes) and a single-knownColor swatch grid built
	// from `branding.colors.primary` so the picker still shows the brand swatch.
	//
	// When ON, the wrapper div below emits `--hub-color-*` vars, and
	// `defaultParameters.backgroundColor` is a `var(--hub-color-primary-200)`
	// reference rather than a hex literal. The renderer drops the var ref into
	// inline style; the cascade resolves it against the wrapper. This means
	// brand-color edits trigger a CSS-only repaint — no React re-render in the
	// consumer is needed for the new color to apply.
	const defaultParameters = useMemo(
		() =>
			isEnabled
				? // Gate ON: delegate to the shared resolver, passing the palette so
					// it returns `var(--hub-color-primary-200)` + the WCAG-readable
					// text color computed against that shade.
					{
						...getHubDefaultParameters({ palette }),
						textColor: primaryTextColor,
					}
				: // Gate OFF: same resolver with no palette → legacy `var(--hub-*)`
					// chain that resolves against setHubBrandingStyle's writes on <html>.
					getHubDefaultParameters(),
		[isEnabled, palette, primaryTextColor],
	);

	// When ON: feed every shade of both palette families into the picker plus
	// universal white/black so the swatch grid surfaces all 22 colors.
	// When OFF: original single-color shape — knownColors holds the legacy
	// CSS-var string (matches master so the picker's strict-equality check
	// highlights the swatch for pre-v2 ADFs that store the same string).
	const brandBackgroundColorMap = useMemo(
		() =>
			isEnabled
				? createBuiltInColorMap({
						knownColors: [...Object.values(palette), '#FFFFFF', '#000000'],
					})
				: createBuiltInColorMap({ knownColors: [defaultParameters.backgroundColor] }),
		[isEnabled, palette, defaultParameters],
	);

	// Border picker swatches: same 20 brand palette + 4 neutrals
	// (white, color.border, color.border.bold, black). We pass the resolved
	// hex values for color.border / color.border.bold so the swatches paint
	// correctly; admin-side translation back to the token reference happens
	// at picker-onChange time in the consumer (see border field code).
	const brandBorderColorMap = useMemo(
		() =>
			isEnabled
				? createBuiltInColorMap({
						knownColors: [
							...Object.values(palette),
							'#FFFFFF',
							HUB_BORDER_NEUTRAL_HEX,
							HUB_BORDER_NEUTRAL_BOLD_HEX,
							'#000000',
						],
					})
				: createBuiltInColorMap({ knownColors: [defaultParameters.backgroundColor] }),
		[isEnabled, palette, defaultParameters],
	);

	const value = useMemo<HubThemingContextValue>(
		() => ({
			isAdvancedThemingEnabled: isEnabled,
			primaryColor,
			accentColor,
			primaryTextColor,
			primaryLinkColor,
			palette,
			defaultParameters,
			brandBackgroundColorMap,
			brandBorderColorMap,
			cornerRadius,
			setHubCornerRadius,
			setHubTypography,
			typography,
			setSpaceDensity,
			spaceDensity,
		}),
		[
			isEnabled,
			primaryColor,
			accentColor,
			primaryTextColor,
			primaryLinkColor,
			palette,
			defaultParameters,
			brandBackgroundColorMap,
			brandBorderColorMap,
			cornerRadius,
			setHubCornerRadius,
			setHubTypography,
			typography,
			setSpaceDensity,
			spaceDensity,
		],
	);

	// Wrapper div emits the entire palette as inline `style` so the 20
	// `--hub-color-*` custom properties land in the SSR HTML and resolve on
	// first paint — no useEffect, no `document.documentElement.setProperty`,
	// no flash of unthemed content. Everything inside the wrapper inherits the
	// vars through the normal CSS cascade.
	//
	// `display: contents` tells the browser to render the wrapper's children
	// but skip generating a box for the wrapper itself, so it is invisible to
	// flex/grid/positioning. Without it, the wrapper would introduce a
	// `display: block` box that could break ancestor layouts (extra flex item,
	// extra grid cell, broken percentage heights, etc.). We get CSS-var
	// scoping + a DOM marker for devtools without any layout side-effect.
	// The cascade still works — `display: contents` does not stop custom
	// properties from being inherited by descendants.
	//
	// Wrapper always emits the full palette + legacy vars, regardless of the
	// gate state. This matters when an admin edits under v2 ON and then toggles
	// the FF off — their persisted ADFs reference `--hub-color-primary-*` and
	// would resolve to `unset` (white) without the wrapper providing the vars.
	// Always emitting keeps a flag flip non-destructive while the resolver and
	// picker still gate their *behavior* on `isAdvancedThemingEnabled`.
	//
	// Legacy compat vars (`--hub-primary-color` / `--hub-primary-text-color` /
	// `--hub-primary-link-color`) are also always emitted so pre-v2 ADFs that
	// reference them render with the exact same colors as before the v2 flip
	// (the input hex verbatim, WCAG-readable text, and
	// `generateThemeBranding(...).color900` link tint). Matches
	// `setHubBrandingStyle`'s semantics so admins with already-customised hubs
	// see no visual change post-flip.
	const wrapperStyle = useMemo<React.CSSProperties>(
		() =>
			({
				display: 'contents',
				...buildPaletteStyle(palette),
				[HUB_PRIMARY_COLOR_CSS_VAR]: primaryColor,
				[HUB_PRIMARY_TEXT_COLOR_CSS_VAR]: primaryTextColor,
				[HUB_PRIMARY_LINK_COLOR_CSS_VAR]: primaryLinkColor,
				[HUB_CORNER_RADIUS_CSS_VAR]: `${cornerRadius}px`,
			}) as React.CSSProperties,
		[palette, primaryColor, primaryTextColor, primaryLinkColor, cornerRadius],
	);

	return (
		<HubThemingContext.Provider value={value}>
			{/* The `style` prop is genuinely dynamic here: it carries the 20
				`--hub-color-*` CSS custom properties derived from the admin's
				chosen brand colors. These must land inline in the SSR HTML so
				they resolve on first paint (a CSS stylesheet would require an
				extra round-trip + injection effect). The `css` prop / styled
				API can't express runtime-generated CSS custom properties. */}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div data-hub-theme-root="" style={wrapperStyle}>
				{children}
			</div>
		</HubThemingContext.Provider>
	);
};

/**
 * Build the inline-style object emitted on the wrapper div from the palette.
 * Returns 20 entries of the form `{ '--hub-color-primary-200': '#B3D4FF' }`.
 *
 * Kept outside the provider so the entry-set construction is testable in
 * isolation and not entangled with React rendering.
 */
const buildPaletteStyle = (
	palette: ReturnType<typeof useHubBrandPalette>,
): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const ref of Object.keys(palette) as PaletteRef[]) {
		out[HUB_COLOR_CSS_VARS[ref]] = palette[ref];
	}
	return out;
};

/**
 * Reads the live theming state.
 *
 * Returns `undefined` if no `<HubThemingProvider>` is in the tree. This is the
 * cross-host safe path: bespoke extension renderers (spotlight, etc.) also
 * run inside Confluence Company Hub where the provider isn't mounted; they
 * read this hook and treat `undefined` as "advanced theming disabled, take
 * the legacy CSS-var passthrough path".
 *
 * Help-center consumers are guaranteed to have a provider in tree (mounted by
 * `HubBootstrap`), so they can safely use the non-null assertion or treat the
 * value as always-defined.
 */
export const useHubTheming = (): HubThemingContextValue | undefined => {
	return useContext(HubThemingContext) ?? undefined;
};

export type { HubDefaultParameters, HubThemingContextValue } from './types';
